import { formatValue, type Widget } from "../api";
import { EmptyPanel, PanelHeader } from "./LineChart";

/**
 * Horizontal bars, because the labels are category names (customers,
 * salespeople) and horizontal bars leave room to read them without rotating
 * text into something nobody can scan.
 */
export function BarChart({
  widget,
  onSelect,
}: {
  widget: Widget;
  /** Clicking a bar drills into it. Absent means the chart is read-only. */
  onSelect?: (label: string) => void;
}) {
  // Always largest-first. The data can arrive in any order (a live drill orders
  // by row count, the precomputed view by insertion); a ranked bar chart should
  // read top-to-bottom regardless.
  const points = [...(Object.values(widget.series)[0] ?? [])].sort(
    (a, b) => Math.abs(b.value ?? 0) - Math.abs(a.value ?? 0),
  );
  if (points.length === 0) return <EmptyPanel widget={widget} />;

  const values = points.map((p) => p.value ?? 0);
  const max = Math.max(...values.map(Math.abs), 1);

  return (
    <div className="panel">
      <PanelHeader widget={widget} />
      <div className="bars">
        {points.map((p) => {
          const value = p.value ?? 0;
          const width = (Math.abs(value) / max) * 100;
          return (
            <div
              className={`bar-row${onSelect ? " clickable" : ""}`}
              key={p.label}
              onClick={onSelect ? () => onSelect(p.label) : undefined}
              role={onSelect ? "button" : undefined}
              tabIndex={onSelect ? 0 : undefined}
              onKeyDown={
                onSelect
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") onSelect(p.label);
                    }
                  : undefined
              }
              title={onSelect ? `Drill into ${p.label}` : p.label}
            >
              <div className="bar-label">{p.label}</div>
              <div className="bar-track">
                <div
                  className={`bar-fill${value < 0 ? " negative" : ""}`}
                  style={{ width: `${width}%` }}
                />
              </div>
              <div className="bar-value">{formatValue(value, widget.format, "short")}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
